import React from 'react'
import PropTypes from 'prop-types'
import {Combobox, useListCollection, Box, Field} from '@chakra-ui/react3'
import {CheckIcon, ChevronUpIcon, ChevronDownIcon, InformationCircleIcon} from '../icons'
import {useIntl} from 'react-intl'

/**
 * Dropdown component built on Chakra UI v3 Combobox.
 * Provides a searchable dropdown with keyboard navigation and accessibility.
 *
 * @component
 * @param {Object} props
 * @param {Array<{label: string, value: string}>} props.items - Array of options to display
 * @param {string} [props.value] - Currently selected value (controlled)
 * @param {function} [props.onValueChange] - Callback when selection changes, receives {value, items}
 * @param {string} [props.placeholder] - Placeholder text for the input
 * @param {string} [props.label] - Accessible label for the dropdown
 * @param {string} [props.variant] - Visual variant: 'outline' | 'filled'
 * @param {boolean} [props.disabled] - Whether the dropdown is disabled
 * @param {boolean} [props.invalid] - Whether the dropdown is in an error state
 * @param {string} [props.errorText] - Error message shown below the dropdown when invalid
 * @param {string} [props.helperText] - Info text shown below the dropdown when not invalid
 */
const DropdownComponent = ({
    items = [],
    value,
    onValueChange,
    placeholder = '',
    label,
    ariaLabel,
    variant = 'outline',
    disabled = false,
    invalid = false,
    errorText,
    helperText,
    ...rest
}) => {
    const {collection, filter} = useListCollection({
        initialItems: items
    })
    const intl = useIntl()

    return (
        <Field.Root invalid={invalid}>
            <Combobox.Root
                collection={collection}
                onInputValueChange={(e) => filter(e.inputValue)}
                defaultValue={value != null ? [String(value)] : undefined}
                onValueChange={onValueChange}
                inputBehavior="none"
                openOnClick
                disabled={disabled}
                invalid={invalid}
                variant={variant}
                {...rest}
            >
                {label && (
                    <Combobox.Label>
                        {label}
                        <Box className="sr-only">{ariaLabel}</Box>
                    </Combobox.Label>
                )}

                <Combobox.Control>
                    <Combobox.Input placeholder={placeholder} />
                    <Combobox.IndicatorGroup>
                        <Combobox.Trigger>
                            <Combobox.Context>
                                {(combobox) =>
                                    combobox.open ? <ChevronUpIcon /> : <ChevronDownIcon />
                                }
                            </Combobox.Context>
                        </Combobox.Trigger>
                    </Combobox.IndicatorGroup>
                </Combobox.Control>

                <Combobox.Positioner>
                    <Combobox.Content>
                        <Combobox.Empty>
                            {intl.formatMessage({
                                id: 'leap.dropdown.no_items',
                                defaultMessage: 'No items found'
                            })}
                        </Combobox.Empty>
                        {collection.items.map((item) => (
                            <Combobox.Item item={item} key={item.value}>
                                <Combobox.ItemText>{item.label}</Combobox.ItemText>
                                <Combobox.ItemIndicator>
                                    <CheckIcon />
                                </Combobox.ItemIndicator>
                            </Combobox.Item>
                        ))}
                    </Combobox.Content>
                </Combobox.Positioner>
            </Combobox.Root>

            {invalid && errorText && (
                <Field.ErrorText>
                    <InformationCircleIcon />
                    {errorText}
                </Field.ErrorText>
            )}
            {!invalid && helperText && (
                <Field.HelperText>
                    <InformationCircleIcon />
                    {helperText}
                </Field.HelperText>
            )}
        </Field.Root>
    )
}

DropdownComponent.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired
        })
    ),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onValueChange: PropTypes.func,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    ariaLabel: PropTypes.string,
    variant: PropTypes.oneOf(['outline', 'filled', 'tertiary', 'cms']),
    disabled: PropTypes.bool,
    invalid: PropTypes.bool,
    errorText: PropTypes.string,
    helperText: PropTypes.string
}

export default DropdownComponent
